/**
 * The asset page's actions. The primary is **Publish** for a draft that meets the publish conditions (with
 * `asset.publish`), otherwise **Edit** (with `asset.manage`); **Refine** (AI, `asset.manage`, not on an archived
 * asset) sits beside it as a secondary. The rest sit in a menu: Confirm still accurate (a
 * published asset, for managers and its active experts), Archive or Unarchive (`asset.archive`), beside the export
 * menu. A refused publish lists what is missing; a stale revision asks for a reload.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Archive, ArchiveRestore, BadgeCheck, Pencil, Send, TriangleAlert, WandSparkles } from 'lucide-react';

import { isStaleRevision, useAsset, useAssetLifecycle, type LifecycleAction } from '@/api/queries';
import type { AssetOut } from '@/api/types';
import { useSession } from '@/session/SessionContext';
import { ActionButton, buttonState } from '@/ui/ActionButton';
import { ConfirmDialog } from '@/ui/ConfirmDialog';
import { RowMenu, type RowMenuItem } from '@/ui/RowMenu';

import { missingText } from '../assetUi';
import { ExportMenu } from '../export/ExportMenu';
import { RefineAssetDialog } from '../refine/RefineAssetDialog';
import { StaleNotice } from '../StaleNotice';

function useMenuItems(asset: AssetOut, run: (action: LifecycleAction) => void, askArchive: () => void): RowMenuItem[] {
  const { can, user } = useSession();
  const expert = asset.experts.some((e) => e.user_id === user?.id && e.is_active);
  const items: RowMenuItem[] = [];
  if (asset.status === 'published' && (can('asset.manage') || expert)) {
    items.push({ label: 'Confirm still accurate', icon: <BadgeCheck aria-hidden="true" />, onSelect: () => run('confirm') });
  }
  if (can('asset.archive') && asset.status !== 'archived') {
    items.push({ label: 'Archive', icon: <Archive aria-hidden="true" />, onSelect: askArchive });
  }
  if (can('asset.archive') && asset.status === 'archived') {
    items.push({ label: 'Unarchive', icon: <ArchiveRestore aria-hidden="true" />, onSelect: () => run('unarchive') });
  }
  return items;
}

/** What still stops a draft from being published, and the server's reason when a publish was refused. */
function PublishBlockers({ asset, refusal }: { asset: AssetOut; refusal: string | null }) {
  const missing = asset.status === 'draft' ? (asset.publish_missing ?? []) : [];
  if (missing.length === 0 && !refusal) return null;
  return (
    <div className="alert alert-warning form-alert publish-blockers" role={refusal ? 'alert' : 'status'}>
      <TriangleAlert aria-hidden="true" />
      <div className="a-body">
        <b>{refusal ? 'Publishing was refused.' : 'Before this draft can be published:'}</b>
        {refusal && <p>{refusal}</p>}
        {missing.length > 0 && (
          <ul>
            {missing.map((m) => <li key={m}>{missingText(m)}</li>)}
          </ul>
        )}
      </div>
    </div>
  );
}

type Lifecycle = ReturnType<typeof useAssetLifecycle>;

interface ButtonsProps {
  asset: AssetOut;
  lifecycle: Lifecycle;
  items: RowMenuItem[];
  onPublish: () => void;
  onRefine: () => void;
}

function Buttons({ asset, lifecycle, items, onPublish, onRefine }: ButtonsProps) {
  const { can } = useSession();
  const canPublish = can('asset.publish') && asset.status === 'draft' && asset.publish_missing?.length === 0;
  return (
    <>
      <ExportMenu asset={asset} />
      {items.length > 0 && <RowMenu label="More actions" items={items} />}
      {can('asset.manage') && asset.status !== 'archived' && (
        <button type="button" className="btn btn-secondary btn-sm" onClick={onRefine}>
          <WandSparkles aria-hidden="true" /> Refine
        </button>
      )}
      {can('asset.manage') && (
        <Link className={`btn ${canPublish ? 'btn-secondary' : 'btn-primary'}`} to={`/assets/${asset.id}/edit`}>
          <Pencil aria-hidden="true" /> Edit
        </Link>
      )}
      {canPublish && (
        <ActionButton
          className="btn-primary"
          state={buttonState(lifecycle)}
          label="Publish"
          icon={<Send aria-hidden="true" />}
          pendingLabel="Publishing…"
          doneLabel="Published"
          onClick={onPublish}
        />
      )}
    </>
  );
}

/** The server's reason when the last publish was refused (a stale revision is a notice of its own). */
export function refusalOf(lifecycle: Pick<Lifecycle, 'error' | 'variables'>): string | null {
  if (lifecycle.variables?.action !== 'publish' || !(lifecycle.error instanceof Error)) return null;
  return isStaleRevision(lifecycle.error) ? null : lifecycle.error.message;
}

function ArchiveDialog({ asset, lifecycle, onClose }: { asset: AssetOut; lifecycle: Lifecycle; onClose: () => void }) {
  return (
    <ConfirmDialog
      title={`Archive ${asset.name}?`}
      confirmLabel="Archive"
      pending={lifecycle.isPending}
      error={lifecycle.error}
      onConfirm={() => lifecycle.mutate({ action: 'archive', revision: asset.revision }, { onSuccess: onClose })}
      onClose={onClose}
    >
      <p>It leaves the catalog's default view and matching. Past results still show it, marked Archived. You can unarchive it later as a draft.</p>
    </ConfirmDialog>
  );
}

/** The page's buttons (for the page head) and its notices (under it). */
export function useAssetActions(asset: AssetOut) {
  const { can } = useSession();
  const lifecycle = useAssetLifecycle(asset.id);
  const reload = useAsset(asset.id);
  const [archiving, setArchiving] = useState(false);
  const [refining, setRefining] = useState(false);
  // A refusal may mean the asset changed under the page (an expert left): re-read what still blocks it.
  const run = (action: LifecycleAction) =>
    lifecycle.mutate({ action, revision: asset.revision }, { onError: (error) => void (isStaleRevision(error) || reload.refetch()) });
  const items = useMenuItems(asset, run, () => setArchiving(true));

  const buttons = <Buttons asset={asset} lifecycle={lifecycle} items={items} onPublish={() => run('publish')} onRefine={() => setRefining(true)} />;
  const notices = (
    <>
      {isStaleRevision(lifecycle.error) && (
        <StaleNotice
          reloading={reload.isFetching}
          onReload={() => {
            lifecycle.reset();
            void reload.refetch();
          }}
        />
      )}
      {can('asset.publish') && <PublishBlockers asset={asset} refusal={refusalOf(lifecycle)} />}
      {archiving && <ArchiveDialog asset={asset} lifecycle={lifecycle} onClose={() => setArchiving(false)} />}
      {refining && <RefineAssetDialog asset={asset} onClose={() => setRefining(false)} />}
    </>
  );
  return { buttons, notices };
}
